import React from 'react';
import {
    Box, Card, CardContent, Typography, Grid, TextField, FormControl,
    InputLabel, Select, MenuItem, Chip, Stack,
    FormControlLabel, Checkbox, useTheme, alpha
} from '@mui/material';
import {
    Shield as ShieldIcon,
    Notifications as NotificationsIcon,
    Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon,
    LocalShipping as LocalShippingIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

export default function D3InterimContainment({ step, onStepUpdate, stepMapping, stepId, isReadOnly, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const updateStep = (updates) => {
        onStepUpdate(stepMapping[stepId], { ...step, ...updates });
    };

    // Validation function to check if all required fields are filled
    const isFormValid = () => {
        // Check that at least some immediate actions are taken
        const hasImmediateActions = step.productionStopped || 
                                   step.inventoryIsolated || 
                                   step.customersNotified || 
                                   step.regulatoryNotification || 
                                   step.managementInformed;

        // Check containment action plan
        const hasContainmentPlan = (step.containmentTypes || []).length > 0 &&
                                  step.responsiblePerson?.trim() &&
                                  step.responsibleDepartment &&
                                  step.urgencyLevel &&
                                  step.implementationDate &&
                                  step.actionDetails?.trim();

        // Check effectiveness verification
        const hasVerification = step.verificationMethods?.trim() &&
                               step.effectivenessRating;

        // Check process & supply chain actions (at least basic fields)
        const hasProcessActions = step.processStatus;

        // Check monitoring & communication plan
        const hasMonitoringPlan = (step.monitoringMethods || []).length > 0 &&
                                 step.monitoringResponsibility &&
                                 (step.communicationStakeholders || []).length > 0 &&
                                 step.communicationMethod &&
                                 step.reviewFrequency &&
                                 step.escalationCriteria;

        return hasImmediateActions && hasContainmentPlan && hasVerification && 
               hasProcessActions && hasMonitoringPlan;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                p: 4, 
                backgroundColor: alpha(theme.palette.warning.main, 0.1), 
                borderRadius: 4, 
                border: `1px solid ${theme.palette.warning.main}`, 
                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`,
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <ShieldIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ 
                            fontWeight: 500, 
                            color: theme.palette.warning.dark, 
                            mb: 0.5
                        }}>
                            D3: Interim Containment Actions
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Implement protective actions to isolate the problem and protect customers while permanent solution is developed.
                        </Typography>
                    </Box>
                </Box>
                
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.error.main, 0.1)}, ${alpha(theme.palette.error.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <NotificationsIcon sx={{ fontSize: 24, color: theme.palette.error.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Immediate Actions Required
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Critical actions to protect customers
                                        </Typography>
                                    </Box>
                                </Box>
                                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={step.productionStopped || false}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ productionStopped: e.target.checked })}
                                                disabled={isReadOnly}
                                                sx={{ 
                                                    color: theme.palette.error.main,
                                                    '&.Mui-checked': { color: theme.palette.error.main }
                                                }}
                                            />
                                        }
                                        label="Production Line Stopped"
                                        sx={{ 
                                            margin: 0,
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '8px 12px',
                                            borderRadius: 2,
                                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                            backgroundColor: step.productionStopped ? alpha(theme.palette.error.main, 0.1) : 'transparent',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.error.main, 0.05)
                                            }
                                        }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={step.inventoryIsolated || false}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ inventoryIsolated: e.target.checked })}
                                                disabled={isReadOnly}
                                                sx={{ 
                                                    color: theme.palette.error.main,
                                                    '&.Mui-checked': { color: theme.palette.error.main }
                                                }}
                                            />
                                        }
                                        label="Affected Inventory Isolated"
                                        sx={{ 
                                            margin: 0,
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '8px 12px',
                                            borderRadius: 2,
                                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                            backgroundColor: step.inventoryIsolated ? alpha(theme.palette.error.main, 0.1) : 'transparent',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.error.main, 0.05)
                                            }
                                        }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={step.customersNotified || false}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ customersNotified: e.target.checked })}
                                                disabled={isReadOnly}
                                                sx={{ 
                                                    color: theme.palette.error.main,
                                                    '&.Mui-checked': { color: theme.palette.error.main }
                                                }}
                                            />
                                        }
                                        label="Affected Customers Notified"
                                        sx={{ 
                                            margin: 0,
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '8px 12px',
                                            borderRadius: 2,
                                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                            backgroundColor: step.customersNotified ? alpha(theme.palette.error.main, 0.1) : 'transparent',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.error.main, 0.05)
                                            }
                                        }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={step.regulatoryNotification || false}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ regulatoryNotification: e.target.checked })}
                                                disabled={isReadOnly}
                                                sx={{ 
                                                    color: theme.palette.error.main,
                                                    '&.Mui-checked': { color: theme.palette.error.main }
                                                }}
                                            />
                                        }
                                        label="Regulatory Bodies Notified (if required)"
                                        sx={{ 
                                            margin: 0,
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '8px 12px',
                                            borderRadius: 2,
                                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                            backgroundColor: step.regulatoryNotification ? alpha(theme.palette.error.main, 0.1) : 'transparent',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.error.main, 0.05)
                                            }
                                        }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={step.managementInformed || false}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ managementInformed: e.target.checked })}
                                                disabled={isReadOnly}
                                                sx={{ 
                                                    color: theme.palette.error.main,
                                                    '&.Mui-checked': { color: theme.palette.error.main }
                                                }}
                                            />
                                        }
                                        label="Senior Management Informed"
                                        sx={{ 
                                            margin: 0,
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '8px 12px',
                                            borderRadius: 2,
                                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                            backgroundColor: step.managementInformed ? alpha(theme.palette.error.main, 0.1) : 'transparent',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.error.main, 0.05)
                                            }
                                        }}
                                    />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Containment Action Plan
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Implementation strategy and timeline
                                        </Typography>
                                    </Box>
                                </Box>
                                <Grid container spacing={3} sx={{ flexGrow: 1 }}>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth sx={{ mb: 3 }}>
                                            <InputLabel>Primary Containment Type</InputLabel>
                                            <Select
                                                multiple
                                                value={step.containmentTypes || []}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ containmentTypes: e.target.value })}
                                                inputProps={{ readOnly: isReadOnly }}
                                                label="Primary Containment Type"
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => (
                                                            <Chip key={value} label={value} size="small" />
                                                        ))}
                                                    </Box>
                                                )}
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[400],
                                                        borderWidth: 1
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[600],
                                                        borderWidth: 1
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[700],
                                                        borderWidth: 1
                                                    }
                                                }}
                                            >
                                                <MenuItem value="Production Hold">Production Hold</MenuItem>
                                                <MenuItem value="Inventory Quarantine">Inventory Quarantine</MenuItem>
                                                <MenuItem value="Supplier Block">Supplier Block</MenuItem>
                                                <MenuItem value="Product Recall">Product Recall</MenuItem>
                                                <MenuItem value="Enhanced Testing">Enhanced Testing</MenuItem>
                                                <MenuItem value="Process Modification">Process Modification</MenuItem>
                                                <MenuItem value="Material Segregation">Material Segregation</MenuItem>
                                                <MenuItem value="Equipment Isolation">Equipment Isolation</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Responsible Person"
                                            value={step.responsiblePerson || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ responsiblePerson: e.target.value })}
                                            InputProps={{ readOnly: isReadOnly }}
                                            placeholder="Name and role"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Department"
                                            value={step.responsibleDepartment || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ responsibleDepartment: e.target.value })}
                                            InputProps={{ readOnly: isReadOnly }}
                                            placeholder="Quality Assurance, Production, etc."
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth>
                                            <InputLabel>Urgency Level</InputLabel>
                                            <Select
                                                value={step.urgencyLevel || ''}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ urgencyLevel: e.target.value })}
                                                inputProps={{ readOnly: isReadOnly }}
                                                label="Urgency Level"
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[400],
                                                        borderWidth: 1
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[600],
                                                        borderWidth: 1
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[700],
                                                        borderWidth: 1
                                                    }
                                                }}
                                            >
                                                <MenuItem value="immediate">Immediate (0-2 hours)</MenuItem>
                                                <MenuItem value="urgent">Urgent (2-24 hours)</MenuItem>
                                                <MenuItem value="high">High (1-3 days)</MenuItem>
                                                <MenuItem value="medium">Medium (3-7 days)</MenuItem>
                                                <MenuItem value="low">Low (&gt; 1 week)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            label="Implementation Date"
                                            value={step.implementationDate ? dayjs(step.implementationDate) : null}
                                            onChange={isReadOnly ? undefined : (newValue) => updateStep({ 
                                                implementationDate: newValue ? newValue.format('YYYY-MM-DD') : null 
                                            })}
                                            disabled={isReadOnly}
                                            format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY' : 'MM/DD/YYYY'}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    sx: {
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            label="Specific Action Details"
                                            value={step.actionDetails || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ actionDetails: e.target.value })}
                                            InputProps={{ readOnly: isReadOnly }}
                                            placeholder="Specific implementation details and steps..."
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <CheckCircleIcon sx={{ fontSize: 24, color: theme.palette.success.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Effectiveness Verification
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Measure containment success
                                        </Typography>
                                    </Box>
                                </Box>
                                <Grid container spacing={3} sx={{ flexGrow: 1 }}>
                                    <Grid item xs={12} md={8}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            label="Verification Methods"
                                            value={step.verificationMethods || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ verificationMethods: e.target.value })}
                                            InputProps={{ readOnly: isReadOnly }}
                                            placeholder="How will you verify containment effectiveness? Include specific tests, metrics, checkpoints, and success criteria..."
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Effectiveness Rating</InputLabel>
                                            <Select
                                                value={step.effectivenessRating || ''}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ effectivenessRating: e.target.value })}
                                                inputProps={{ readOnly: isReadOnly }}
                                                label="Effectiveness Rating"
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[400],
                                                        borderWidth: 1
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[600],
                                                        borderWidth: 1
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[700],
                                                        borderWidth: 1
                                                    }
                                                }}
                                            >
                                                <MenuItem value="not_started">Not Started</MenuItem>
                                                <MenuItem value="in_progress">In Progress</MenuItem>
                                                <MenuItem value="partially_effective">Partially Effective</MenuItem>
                                                <MenuItem value="fully_effective">Fully Effective</MenuItem>
                                                <MenuItem value="ineffective">Ineffective - Needs Revision</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <LocalShippingIcon sx={{ fontSize: 24, color: theme.palette.info.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Process & Supply Chain Actions
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Operational control measures
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ minHeight: 120, display: 'flex', flexDirection: 'column' }}>
                                            <FormControl fullWidth sx={{ mb: 3 }}>
                                                <InputLabel>Supplier Actions</InputLabel>
                                                <Select
                                                    multiple
                                                    value={step.supplierActions || []}
                                                    onChange={isReadOnly ? undefined : (e) => updateStep({ supplierActions: e.target.value })}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Supplier Actions"
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => (
                                                                <Chip key={value} label={value} size="small" />
                                                            ))}
                                                        </Box>
                                                    )}
                                                    sx={{
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[400],
                                                            borderWidth: 1
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[600],
                                                            borderWidth: 1
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[700],
                                                            borderWidth: 1
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="Supplier Audit">Supplier Audit</MenuItem>
                                                    <MenuItem value="Material Hold">Material Hold</MenuItem>
                                                    <MenuItem value="Certificate Review">Certificate Review</MenuItem>
                                                    <MenuItem value="Enhanced Inspection">Enhanced Inspection</MenuItem>
                                                    <MenuItem value="Alternative Supplier">Alternative Supplier</MenuItem>
                                                    <MenuItem value="Supplier CAPA">Supplier CAPA</MenuItem>
                                                    <MenuItem value="Contract Modification">Contract Modification</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        <FormControl fullWidth>
                                            <InputLabel>Supplier Communication</InputLabel>
                                            <Select
                                                value={step.supplierCommunication || ''}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ supplierCommunication: e.target.value })}
                                                inputProps={{ readOnly: isReadOnly }}
                                                label="Supplier Communication"
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[400],
                                                        borderWidth: 1
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[600],
                                                        borderWidth: 1
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[700],
                                                        borderWidth: 1
                                                    }
                                                }}
                                            >
                                                <MenuItem value="notification_sent">Notification Sent</MenuItem>
                                                <MenuItem value="meeting_scheduled">Meeting Scheduled</MenuItem>
                                                <MenuItem value="action_plan_received">Action Plan Received</MenuItem>
                                                <MenuItem value="pending_response">Pending Response</MenuItem>
                                                <MenuItem value="not_applicable">Not Applicable</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ minHeight: 120, display: 'flex', flexDirection: 'column' }}>
                                        <FormControl fullWidth sx={{ mb: 3 }}>
                                            <InputLabel>Manufacturing Controls</InputLabel>
                                            <Select
                                                multiple
                                                value={step.manufacturingControls || []}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ manufacturingControls: e.target.value })}
                                                inputProps={{ readOnly: isReadOnly }}
                                                label="Manufacturing Controls"
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => (
                                                            <Chip key={value} label={value} size="small" />
                                                        ))}
                                                    </Box>
                                                )}
                                                sx={{ 
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[400],
                                                        borderWidth: 1
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[600],
                                                        borderWidth: 1
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[700],
                                                        borderWidth: 1
                                                    }
                                                }}
                                            >
                                                <MenuItem value="Process Hold">Process Hold</MenuItem>
                                                <MenuItem value="Enhanced Monitoring">Enhanced Monitoring</MenuItem>
                                                <MenuItem value="Parameter Adjustment">Parameter Adjustment</MenuItem>
                                                <MenuItem value="Equipment Calibration">Equipment Calibration</MenuItem>
                                                <MenuItem value="Operator Retraining">Operator Retraining</MenuItem>
                                                <MenuItem value="Line Clearance">Line Clearance</MenuItem>
                                                <MenuItem value="Batch-by-Batch Release">Batch-by-Batch Release</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <FormControl fullWidth>
                                        <InputLabel>Process Status</InputLabel>
                                        <Select
                                            value={step.processStatus || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ processStatus: e.target.value })}
                                            inputProps={{ readOnly: isReadOnly }}
                                            label="Process Status"
                                            sx={{ 
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[400],
                                                    borderWidth: 1
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[600],
                                                    borderWidth: 1
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[700],
                                                    borderWidth: 1
                                                }
                                            }}
                                        >
                                            <MenuItem value="stopped">Stopped</MenuItem>
                                            <MenuItem value="modified">Modified</MenuItem>
                                            <MenuItem value="enhanced_control">Enhanced Control</MenuItem>
                                            <MenuItem value="normal_operation">Normal Operation</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ minHeight: 120, display: 'flex', flexDirection: 'column' }}>
                                        <FormControl fullWidth sx={{ mb: 3 }}>
                                            <InputLabel>Quality Control Measures</InputLabel>
                                            <Select
                                                multiple
                                                value={step.qualityControlMeasures || []}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ qualityControlMeasures: e.target.value })}
                                                inputProps={{ readOnly: isReadOnly }}
                                                label="Quality Control Measures"
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => (
                                                            <Chip key={value} label={value} size="small" />
                                                        ))}
                                                    </Box>
                                                )}
                                                sx={{ 
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[400],
                                                        borderWidth: 1
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[600],
                                                        borderWidth: 1
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[700],
                                                        borderWidth: 1
                                                    }
                                                }}
                                            >
                                                <MenuItem value="Enhanced Testing">Enhanced Testing</MenuItem>
                                                <MenuItem value="100% Inspection">100% Inspection</MenuItem>
                                                <MenuItem value="Statistical Sampling">Statistical Sampling</MenuItem>
                                                <MenuItem value="Third-Party Testing">Third-Party Testing</MenuItem>
                                                <MenuItem value="Microbial Testing">Microbial Testing</MenuItem>
                                                <MenuItem value="Stability Testing">Stability Testing</MenuItem>
                                                <MenuItem value="Release Hold">Release Hold</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <FormControl fullWidth>
                                        <InputLabel>Testing Frequency</InputLabel>
                                        <Select
                                            value={step.testingFrequency || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ testingFrequency: e.target.value })}
                                            inputProps={{ readOnly: isReadOnly }}
                                            label="Testing Frequency"
                                            sx={{ 
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[400],
                                                    borderWidth: 1
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[600],
                                                    borderWidth: 1
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[700],
                                                    borderWidth: 1
                                                }
                                            }}
                                        >
                                            <MenuItem value="every_batch">Every Batch</MenuItem>
                                            <MenuItem value="daily">Daily</MenuItem>
                                            <MenuItem value="weekly">Weekly</MenuItem>
                                            <MenuItem value="per_lot">Per Lot</MenuItem>
                                            <MenuItem value="standard">Standard Protocol</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <VisibilityIcon sx={{ fontSize: 24, color: theme.palette.info.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Monitoring & Communication Plan
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Ongoing oversight and stakeholder updates
                                        </Typography>
                                    </Box>
                                </Box>
                                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth sx={{ mb: 3 }}>
                                        <InputLabel>Monitoring Methods</InputLabel>
                                        <Select
                                            multiple
                                            value={step.monitoringMethods || []}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ monitoringMethods: e.target.value })}
                                            inputProps={{ readOnly: isReadOnly }}
                                            label="Monitoring Methods"
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => (
                                                        <Chip key={value} label={value} />
                                                    ))}
                                                </Box>
                                            )}
                                            sx={{ 
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[400],
                                                    borderWidth: 1
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[600],
                                                    borderWidth: 1
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[700],
                                                    borderWidth: 1
                                                }
                                            }}
                                        >
                                            <MenuItem value="Statistical Process Control">Statistical Process Control</MenuItem>
                                            <MenuItem value="Real-time Testing">Real-time Testing</MenuItem>
                                            <MenuItem value="Visual Inspection">Visual Inspection</MenuItem>
                                            <MenuItem value="Customer Feedback">Customer Feedback</MenuItem>
                                            <MenuItem value="Supplier Reports">Supplier Reports</MenuItem>
                                            <MenuItem value="Quality Metrics">Quality Metrics</MenuItem>
                                            <MenuItem value="Inventory Tracking">Inventory Tracking</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <InputLabel>Monitoring Responsibility</InputLabel>
                                        <Select
                                            value={step.monitoringResponsibility || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ monitoringResponsibility: e.target.value })}
                                            inputProps={{ readOnly: isReadOnly }}
                                            label="Monitoring Responsibility"
                                            sx={{ 
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[400],
                                                    borderWidth: 1
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[600],
                                                    borderWidth: 1
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[700],
                                                    borderWidth: 1
                                                }
                                            }}
                                        >
                                            <MenuItem value="Quality Assurance">Quality Assurance</MenuItem>
                                            <MenuItem value="Production Supervisor">Production Supervisor</MenuItem>
                                            <MenuItem value="Process Engineer">Process Engineer</MenuItem>
                                            <MenuItem value="Quality Engineer">Quality Engineer</MenuItem>
                                            <MenuItem value="Team Lead">Team Lead</MenuItem>
                                            <MenuItem value="Cross-functional Team">Cross-functional Team</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth sx={{ mb: 3 }}>
                                        <InputLabel>Communication Stakeholders</InputLabel>
                                        <Select
                                            multiple
                                            value={step.communicationStakeholders || []}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ communicationStakeholders: e.target.value })}
                                            inputProps={{ readOnly: isReadOnly }}
                                            label="Communication Stakeholders"
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => (
                                                        <Chip key={value} label={value} />
                                                    ))}
                                                </Box>
                                            )}
                                            sx={{ 
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[400],
                                                    borderWidth: 1
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[600],
                                                    borderWidth: 1
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[700],
                                                    borderWidth: 1
                                                }
                                            }}
                                        >
                                            <MenuItem value="Senior Management">Senior Management</MenuItem>
                                            <MenuItem value="Quality Director">Quality Director</MenuItem>
                                            <MenuItem value="Production Manager">Production Manager</MenuItem>
                                            <MenuItem value="Regulatory Affairs">Regulatory Affairs</MenuItem>
                                            <MenuItem value="Customers">Customers</MenuItem>
                                            <MenuItem value="Suppliers">Suppliers</MenuItem>
                                            <MenuItem value="Legal Team">Legal Team</MenuItem>
                                            <MenuItem value="External Auditors">External Auditors</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <InputLabel>Communication Method</InputLabel>
                                        <Select
                                            value={step.communicationMethod || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ communicationMethod: e.target.value })}
                                            inputProps={{ readOnly: isReadOnly }}
                                            label="Communication Method"
                                            sx={{ 
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[400],
                                                    borderWidth: 1
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[600],
                                                    borderWidth: 1
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[700],
                                                    borderWidth: 1
                                                }
                                            }}
                                        >
                                            <MenuItem value="email_reports">Email Reports</MenuItem>
                                            <MenuItem value="dashboard_updates">Dashboard Updates</MenuItem>
                                            <MenuItem value="meetings">Regular Meetings</MenuItem>
                                            <MenuItem value="phone_calls">Phone Calls</MenuItem>
                                            <MenuItem value="formal_notifications">Formal Notifications</MenuItem>
                                            <MenuItem value="automated_alerts">Automated Alerts</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        type="number"
                                        label="Estimated Duration (days)"
                                        value={step.estimatedDuration || ''}
                                        onChange={isReadOnly ? undefined : (e) => updateStep({ estimatedDuration: e.target.value })}
                                        InputProps={{ readOnly: isReadOnly }}
                                        placeholder="0"
                                        fullWidth
                                        sx={{ 
                                            mb: 2,
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        type="number"
                                        label="Cost of Containment ($)"
                                        value={step.containmentCost || ''}
                                        onChange={isReadOnly ? undefined : (e) => updateStep({ containmentCost: e.target.value })}
                                        InputProps={{ readOnly: isReadOnly }}
                                        placeholder="0"
                                        fullWidth
                                        sx={{ 
                                            mb: 2,
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Review Frequency</InputLabel>
                                        <Select
                                            value={step.reviewFrequency || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ reviewFrequency: e.target.value })}
                                            inputProps={{ readOnly: isReadOnly }}
                                            label="Review Frequency"
                                        >
                                            <MenuItem value="hourly">Hourly</MenuItem>
                                            <MenuItem value="daily">Daily</MenuItem>
                                            <MenuItem value="weekly">Weekly</MenuItem>
                                            <MenuItem value="bi_weekly">Bi-weekly</MenuItem>
                                            <MenuItem value="monthly">Monthly</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Escalation Criteria</InputLabel>
                                        <Select
                                            value={step.escalationCriteria || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ escalationCriteria: e.target.value })}
                                            inputProps={{ readOnly: isReadOnly }}
                                            label="Escalation Criteria"
                                        >
                                            <MenuItem value="immediate">Immediate Escalation</MenuItem>
                                            <MenuItem value="trend_worsening">Trend Worsening</MenuItem>
                                            <MenuItem value="customer_complaint">Customer Complaint</MenuItem>
                                            <MenuItem value="regulatory_interest">Regulatory Interest</MenuItem>
                                            <MenuItem value="cost_threshold">Cost Threshold Exceeded</MenuItem>
                                            <MenuItem value="timeline_delay">Timeline Delay</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            label="Additional Monitoring Notes"
                                            value={step.monitoringNotes || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ monitoringNotes: e.target.value })}
                                            InputProps={{ readOnly: isReadOnly }}
                                            placeholder="Any specific monitoring details or special instructions..."
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            p: 3, 
                            backgroundColor: 'white',
                            borderRadius: 4, 
                            border: `1px solid ${isFormValid() ? theme.palette.success.main : theme.palette.grey[400]}`,
                            boxShadow: `0 8px 32px ${alpha(isFormValid() ? theme.palette.success.main : theme.palette.grey[500], 0.12)}`,
                            backdropFilter: 'blur(20px)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={step.completed || false}
                                        onChange={isReadOnly ? undefined : (e) => {
                                            if (!isFormValid() && e.target.checked) {
                                                alert('Please fill out all required fields before marking as complete.');
                                                return;
                                            }
                                            updateStep({ completed: e.target.checked });
                                        }}
                                        disabled={isReadOnly || !isFormValid()}
                                        sx={{ 
                                            '& .MuiSvgIcon-root': { 
                                                fontSize: 28, 
                                                color: isFormValid() ? theme.palette.success.main : theme.palette.grey[400] 
                                            } 
                                        }}
                                    />
                                }
                                label=""
                            />
                            <Typography variant="h6" sx={{ 
                                fontWeight: 500, 
                                color: isFormValid() ? theme.palette.success.main : theme.palette.grey[600] 
                            }}>
                                D3 Interim Containment Complete - Ready for Root Cause Analysis
                            </Typography>
                            {!isFormValid() && (
                                <Typography variant="body2" sx={{ color: theme.palette.error.main, fontStyle: 'italic' }}>
                                    (Complete all required fields to enable)
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </LocalizationProvider>
    );
} 